import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { setReportDevicesFilter } from '../../../../../../../../../../services/redux/actions';
import { injectIntl } from 'react-intl';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Divider from 'material-ui/Divider';
import { grey400 } from 'material-ui/styles/colors';
import withQueryStringProps from '../../../../../../../../../../components/withQueryStringProps';
import HideIfPrint from '../../../../../../../../../../components/HideIfPrint';
import ShowIfPrint from '../../../../../../../../../../components/ShowIfPrint';
import _ from 'lodash';
import allowedDevicesService from '../../../../../../../../../../services/allowedDevices';


function getAllowedDeviceIds(props) {
  return allowedDevicesService.getAllowedDevices(props.relayDevices, props.viewer.devices, props.report.devices).map(({ id }) => id);
}

function collapseLocations(allDevices) {
  const [devices, locationDevices] = _.partition(allDevices, d => !d.location);
  const locationsObj = _.groupBy(locationDevices, d => d.location);
  const locations = _.toPairs(locationsObj).map(([location, devices]) => {
    return {
      name: location,
      id: 'location:' + location,
    };
  });
  return _.sortBy([...devices, ...locations], 'name');
}

function expandItemIds(itemIds, relayDevices) {
  const deviceEdges = (relayDevices && relayDevices.edges) || [];
  const deviceNodes = deviceEdges.map(edge => edge.node);
  return itemIds.reduce((arr, id) => {
    if (_.includes(id, 'location:')) {
      const ids = deviceNodes
        .filter(d => d.location === _.split(id, 'location:')[1])
        .map(d => d.id);
      return [...arr, ...ids];
    } else {
      return [...arr, id];
    }
  }, []);
}

const styles = {
  deviceSelectField: {
    verticalAlign: 'middle',
    width: 350,
    icon: {
      fill: grey400,
    },
    underline: {
      borderColor: grey400,
    },
  },
};

@withQueryStringProps(['devices'])
@injectIntl
@connect()
class DevicePicker extends Component {
  constructor(props) {
    super(props);
    const selectedDevices = _.intersection(props.devices, getAllowedDeviceIds(props));
    this.state = {
      selectedDevices: (selectedDevices && selectedDevices.length > 0)
        ? selectedDevices
        : ['all'],
    };
  }

  handleSelectedDeviceChange = (event, index, values) => {
    const selectedDevices = expandItemIds(values, this.props.relayDevices);
    if (selectedDevices.length <= 0) {
      this.setState({ selectedDevices: ['all'] });
    } else if (_.includes(this.state.selectedDevices, 'all') && selectedDevices.length > 1) {
      this.setState({ selectedDevices: _.without(selectedDevices, 'all') });
    } else if (_.includes(selectedDevices, 'all')) {
      this.setState({ selectedDevices: ['all'] });
    } else {
      this.setState({ selectedDevices: selectedDevices });
    }
  };

  setDevicesQueryStringAndReduxState = () => {
    let deviceIds = [];
    if (_.isEqual(this.state.selectedDevices, ['all'])) {
      deviceIds = getAllowedDeviceIds(this.props);
      this.props.qs.set({ devices: null });
    } else {
      deviceIds = this.state.selectedDevices;
      this.props.qs.set({ devices: this.state.selectedDevices });
    }
    this.props.dispatch(setReportDevicesFilter(this.props.report.id, deviceIds));
  };

  componentDidMount() {
    this.setDevicesQueryStringAndReduxState();
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.devices, nextProps.devices)) {
      this.setState({
        selectedDevices: nextProps.devices || ['all'],
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !_.isEqual(prevState.selectedDevices, this.state.selectedDevices) ||
      prevProps.report.groupByLocation !== this.props.report.groupByLocation ||
      !_.isEqual(prevProps.report.devices, this.props.report.devices)
    ) {
      this.setDevicesQueryStringAndReduxState();
    }
  }

  render() {
    const allDevices = allowedDevicesService.getAllowedDevices(this.props.relayDevices, this.props.viewer.devices, this.props.report.devices);

    const menuItems = this.props.report.groupByLocation
      ? collapseLocations(allDevices)
      : allDevices;

    const selectedItems = this.props.report.groupByLocation
      ? _.isEqual(this.state.selectedDevices, ['all'])
        ? ['all']
        : collapseLocations(
          allDevices.filter(d => _.includes(this.state.selectedDevices, d.id))
        ).map(x => x.id)
      : this.state.selectedDevices;

    if (menuItems.length <= 1) {
      return null;
    } else {
      return (
        <div>
          <HideIfPrint>
            <SelectField
              multiple={true}
              value={selectedItems}
              onChange={this.handleSelectedDeviceChange}
              autoWidth={true}
              style={styles.deviceSelectField}
              iconStyle={styles.deviceSelectField.icon}
              underlineStyle={styles.deviceSelectField.underline}
            >
              <MenuItem
                key="all"
                value="all"
                primaryText={this.props.intl.formatMessage({ id: 'allTerminals' })}
                insetChildren={true}
                checked={selectedItems && selectedItems.indexOf('all') > -1}
              />
              <Divider />
              {menuItems.map(item =>
                <MenuItem
                  key={item.id}
                  value={item.id}
                  primaryText={item.name}
                  insetChildren={true}
                  checked={selectedItems && selectedItems.indexOf(item.id) > -1}
                />
              )}
            </SelectField>
          </HideIfPrint>
          <ShowIfPrint>
            <span>
              {_.isEqual(selectedItems, ['all']) ? (
                this.props.intl.formatMessage({ id: 'allTerminals' })
              ) : (
                <span>
                  {this.props.intl.formatMessage({ id: 'terminals' }) + ': '}
                  {menuItems.filter(x => _.includes(selectedItems, x.id)).map((item, i) => {
                    if (i === 0) {
                      return <span key={item.id}>{item.name}</span>;
                    } else {
                      return <span key={item.id}>, {item.name}</span>;
                    }
                  })}
                </span>
              )}
            </span>
          </ShowIfPrint>
        </div>
      );
    }
  }
}

export default createFragmentContainer(
  DevicePicker,
  graphql`
    fragment DevicePicker_viewer on Viewer {
      devices {
        id
        name
        location
      }
    }
    fragment DevicePicker_report on Report {
      id
      groupByLocation
      devices {
        id
        name
        location
      }
    }
    fragment DevicePicker_relayDevices on DeviceConnection {
      edges {
        node {
          id
          name
          location
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/scenes/Report/components/ReportWrapper/components/ReportHeader/components/ReportFilters/components/DevicePicker/DevicePicker.js